import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { rejoinableSessions, type SessionLimits } from '@teams-avatar/shared';
import { api, ApiError } from './lib/api';
import { splitSessions, useSessionStream } from './lib/useSessionStream';
import { ConnectionIndicator } from './components/ConnectionIndicator';
import { HealthPanel } from './components/HealthPanel';
import { LobbyBanner } from './components/LobbyBanner';
import { LoginView } from './components/LoginView';
import { LogPanel } from './components/LogPanel';
import { AudioMeter } from './components/AudioMeter';
import { SessionTable } from './components/SessionTable';
import { StartSessionForm } from './components/StartSessionForm';
import { Button, SectionTitle, Stat, Wordmark } from './components/ui';

export function App(): JSX.Element {
  const [authState, setAuthState] = useState<'unknown' | 'out' | 'in'>('unknown');
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    void api
      .me()
      .then((result) => {
        setAuthState(result.authenticated ? 'in' : 'out');
        setUserName(result.user?.name ?? null);
      })
      .catch(() => setAuthState('out'));
  }, []);

  if (authState === 'unknown') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-sm text-fg-muted">Lade …</p>
      </div>
    );
  }

  if (authState === 'out') {
    return (
      <LoginView
        onLoggedIn={(name) => {
          setUserName(name);
          setAuthState('in');
        }}
      />
    );
  }

  return <Dashboard userName={userName} onLoggedOut={() => setAuthState('out')} />;
}

function Dashboard({
  userName,
  onLoggedOut,
}: {
  userName: string | null;
  onLoggedOut: () => void;
}): JSX.Element {
  const stream = useSessionStream();
  const { active, history } = splitSessions(stream.sessions);
  // Nur die juengsten beendeten Sessions bekommen einen erneuten Beitritt —
  // die Regel samt Begruendung steht in shared/session.ts.
  const rejoinbar = new Set(rejoinableSessions(stream.sessions).map((s) => s.id));
  const inLobby = active.filter((session) => session.state === 'lobby');

  const [limits, setLimits] = useState<SessionLimits | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openLogId, setOpenLogId] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<{ id: string; url: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Kurze Rueckmeldung, dass die Taste angekommen ist. */
  const [tasteAn, setTasteAn] = useState<string | null>(null);

  const refreshLimits = useCallback(() => {
    void api
      .sessions()
      .then((result) => setLimits(result.limits))
      .catch(() => undefined);
  }, []);

  useEffect(refreshLimits, [refreshLimits, stream.sessions.length]);

  const stop = async (id: string): Promise<void> => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.stopSession(id);
    } catch (cause) {
      setActionError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Taste "m" an eine bestimmte Session schicken.
   *
   * Knopf und Tastatur nehmen denselben Weg — sonst laufen die beiden Pfade
   * frueher oder spaeter auseinander.
   */
  const sendeTaste = useCallback((id: string): void => {
    setActionError(null);
    void api
      .sendKey(id, 'm')
      .then(() => {
        setTasteAn(id);
        window.setTimeout(() => setTasteAn(null), 1200);
      })
      .catch((cause: unknown) => {
        setActionError(cause instanceof ApiError ? cause.message : String(cause));
      });
  }, []);

  /*
   * Taste "m" an die AVA-Seite durchreichen.
   *
   * Dort schaltet sie im Realtime-Werkzeug das Mikrofon auf und zu. Die
   * Oberflaeche weiss nicht, was die Taste bewirkt, und soll es auch nicht
   * wissen — sie reicht sie weiter.
   *
   * Zielsession: die mit offenem Panel, sonst die einzige laufende. Gibt es
   * mehrere und keine ausgewaehlte, passiert nichts: eine zufaellige zu
   * erwischen waere schlimmer als gar nichts zu tun.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'm' && event.key !== 'M') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const ziel = event.target as HTMLElement | null;
      const tag = ziel?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || ziel?.isContentEditable === true) return;

      const id = openLogId ?? (active.length === 1 ? active[0]?.id : undefined);
      if (id === undefined) {
        setActionError(
          active.length === 0
            ? 'Keine laufende Session — die Taste „m“ hat nichts, wohin sie gehen koennte.'
            : 'Mehrere Sessions laufen. Bitte erst eine mit „Ton & Log“ auswaehlen.',
        );
        return;
      }
      event.preventDefault();
      sendeTaste(id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openLogId, active, sendeTaste]);

  const rejoin = async (id: string): Promise<void> => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.rejoinSession(id);
      refreshLimits();
    } catch (cause) {
      setActionError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusyId(null);
    }
  };

  const toggleLog = (id: string): void => {
    if (openLogId === id) {
      stream.unsubscribe(id);
      setOpenLogId(null);
      return;
    }
    if (openLogId !== null) stream.unsubscribe(openLogId);
    stream.subscribe(id);
    setOpenLogId(id);
  };

  const frei = limits === null ? null : limits.maxConcurrentSessions - active.length;

  return (
    <div className="min-h-dvh">
      <header className="border-b border-border bg-bg">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3.5">
          <Wordmark />
          <div className="flex items-center gap-3">
            <ConnectionIndicator state={stream.connection} />
            {userName !== null && (
              <span className="hidden text-sm text-fg-subtle sm:inline">{userName}</span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void api.logout().then(onLoggedOut)}
            >
              abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-5 px-5 py-6">
        <LobbyBanner sessions={inLobby} />

        {/*
          Kennzahlenreihe nach dem Muster des Kits. Sie beantwortet die zwei
          Fragen, die man vor dem Starten hat: laeuft schon etwas, und ist noch
          Platz?
        */}
        <div className="grid grid-cols-2 divide-x divide-y divide-border overflow-hidden rounded-xl border border-border bg-bg sm:grid-cols-4 sm:divide-y-0">
          <Stat value={active.length} label="laufende Sessions" />
          <Stat
            value={frei === null ? '–' : frei}
            label="freie Plaetze"
            tone={frei === 0 ? 'danger' : 'neutral'}
          />
          <Stat value={inLobby.length} label="in der Lobby" />
          <Stat value={history.length} label="beendet" />
        </div>

        <StartSessionForm limits={limits} onStarted={refreshLimits} />

        {actionError !== null && (
          <p className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{actionError}</p>
        )}

        {tasteAn !== null && (
          <p className="rounded-lg bg-brand px-3 py-2 text-sm text-fg" role="status">
            Taste „m“ an AVA geschickt.
          </p>
        )}

        <section className="space-y-3">
          <SectionTitle
            right={
              limits === null ? undefined : `${active.length} von ${limits.maxConcurrentSessions}`
            }
          >
            Laufende Sessions
          </SectionTitle>
          {active.length > 0 && (
            <p className="text-xs text-fg-muted">
              „Mikro“ schaltet in goDialogue 2.0 das Mikrofon auf und zu. Am Rechner geht auch die
              Taste <kbd className="rounded border border-border bg-bg px-1.5 py-0.5 font-mono">m</kbd>
              {active.length > 1 ? ' — dann vorher eine Session mit „Ton & Log“ auswaehlen.' : '.'}
            </p>
          )}
          <SessionTable
            sessions={active}
            variant="active"
            emptyMessage="Keine laufenden Sessions."
            onStop={(id) => void stop(id)}
            onScreenshot={(id) => setScreenshot({ id, url: api.screenshotUrl(id) })}
            onToggleLog={toggleLog}
            onMic={sendeTaste}
            micSentId={tasteAn}
            busyId={busyId}
            openLogId={openLogId}
          />
          {openLogId !== null && (
            <div className="space-y-3">
              <AudioMeter audio={stream.audio[openLogId]} />
              <LogPanel lines={stream.logs[openLogId] ?? []} onClose={() => toggleLog(openLogId)} />
            </div>
          )}
        </section>

        <section className="space-y-3">
          <SectionTitle>Historie</SectionTitle>
          <SessionTable
            sessions={history}
            variant="history"
            emptyMessage="Noch keine beendeten Sessions."
            onRejoin={(id) => void rejoin(id)}
            rejoinableIds={rejoinbar}
            busyId={busyId}
          />
        </section>

        <HealthPanel />

        <footer className="pt-2 pb-6 text-xs text-fg-muted">
          Es wird nichts aufgezeichnet und kein Transkript gespeichert. Audio wird nur transient
          verarbeitet.
        </footer>
      </main>

      {screenshot !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/70 p-4 sm:p-6"
          onClick={() => setScreenshot(null)}
        >
          <div
            className="max-h-full w-full max-w-5xl overflow-auto rounded-xl bg-bg p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-xs text-fg-muted">Was im Meeting ankommt</span>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setScreenshot({ id: screenshot.id, url: api.screenshotUrl(screenshot.id) })
                  }
                >
                  neu laden
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setScreenshot(null)}
                >
                  schliessen
                </Button>
              </div>
            </div>
            {/* Schlaegt der Abruf fehl, zeigt der Browser das gebrochene Bild —
                der Grund steht dann im Live-Log der Session. */}
            <img
              src={screenshot.url}
              alt="Aktuelles Bild aus dem Meeting"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
